import React from "react";
import { Navigation, Runners } from "@hex-insights/app-modules";
import {
	Button,
	Column,
	Drawer,
	Image,
	Row,
	Tooltip,
	TooltipButton,
	useMaxStackIndex,
	useMediaQuery,
} from "@hex-insights/core";
import { InternalLink, NavLink } from "@hex-insights/router";
import {
	Icon,
	MessageComponents,
	ModelComponents,
	StaticURLs,
	useInternalPortalsViewerInfoQuery,
	UserComponents,
	UserUtilities,
} from "@s-i-s-i/see-learning.shared";
import { logoutPageInfo } from "../../Pages/Authentication/Logout/pageinfo";
import { homePageInfo } from "../../Pages/Home/pageinfo";
import { messagingPageInfo } from "../../Pages/Messaging/pageinfo";
import { preferencesPageInfo } from "../../Pages/Preferences/pageinfo";
import { appVersion, instanceID } from "../../settings";
import styles from "./styles.module.css";

export function Header() {
	return (
		<React.Fragment>
			<Navigation.SkipToContentLink />
			<Runners.Header Bar={HeaderBar} Drawer={HeaderDrawerController} SideBar={HeaderSideBar} className="no-print" />
		</React.Fragment>
	);
}

function HeaderBar({ toggleDrawer }: Runners.HeaderBarProps) {
	const { data: viewerData } = useInternalPortalsViewerInfoQuery();

	return (
		<div className={styles["header__bar"]}>
			<div className={styles["header__bar__background"]}></div>
			<Row justify="space-between" align="center" className={styles["header__bar__pill"]}>
				<Button variant="secondary" onClick={toggleDrawer} className={styles["header__bar__drawer-button"]}>
					<Icon.List size={16} className="block" />
				</Button>

				<Row justify="spaced-start" align="center" className={styles["header__bar__title-and-search"]}>
					<InternalLink to={homePageInfo.to} className={styles["header__bar__site-title"]}>
						<Image
							src={StaticURLs.Images.seeLearningLogo}
							sizing="height-scaled"
							style={{ height: "2.25rem" }}
							title="SEE Learning Portal"
						/>
					</InternalLink>
				</Row>

				<Row justify="spaced-end" align="center" horizontalSpacing="3rem">
					<Row justify="spaced-end" align="center">
						<nav className={styles["header__bar__nav"]}>
							<Row justify="spaced-start" align="center" horizontalSpacing="1.5rem">
								<NavLink to={homePageInfo.to} exact={homePageInfo.exact} className={styles["header__bar__nav__link"]}>
									<Column justify="spaced-start" align="center" verticalSpacing="0.125rem">
										<Icon.House />
										<span className={styles["header__bar__nav__link__text"]}>Home</span>
									</Column>
								</NavLink>
								<NavLink
									to={messagingPageInfo.to}
									exact={messagingPageInfo.exact}
									className={styles["header__bar__nav__link"]}
								>
									<Column justify="spaced-start" align="center" verticalSpacing="0.125rem">
										<span className={styles["header__bar__nav__link__icon__wrapper"]}>
											<MessageComponents.ControlledMessageAlertIcon
												messagingPagePath={messagingPageInfo.path as string}
												className={styles["header__bar__nav__link__icon__alert"]}
											/>
											<Icon.Chat />
										</span>
										<span className={styles["header__bar__nav__link__text"]}>Messaging</span>
									</Column>
								</NavLink>
							</Row>
						</nav>
					</Row>

					<TooltipButton>
						{() => (
							<Tooltip isInteractable className={styles["user-tooltip"]}>
								<Tooltip.Body className={styles["user-tooltip__body"]}>
									<Column justify="spaced-start" align="center">
										<NavLink
											to={preferencesPageInfo.to}
											exact={preferencesPageInfo.exact}
											className={styles["user-tooltip__link"]}
										>
											Preferences
										</NavLink>
										<NavLink
											to={logoutPageInfo.to}
											exact={logoutPageInfo.exact}
											className={styles["user-tooltip__link"]}
										>
											Logout
										</NavLink>
									</Column>
								</Tooltip.Body>
								<Tooltip.Footer className={styles["user-tooltip__footer"]}>
									<Row justify="space-between">
										<span className={styles["app-info"]}>v{appVersion}</span>
										<span className={styles["app-info"]} title={instanceID}>
											{instanceID.split("-")[0]}
										</span>
									</Row>
								</Tooltip.Footer>
							</Tooltip>
						)}
						{({ onClick, disabled }) => (
							<Button onClick={onClick} disabled={disabled} className={styles["user-button"]}>
								<UserComponents.UserIcon user={viewerData?.viewer ?? UserUtilities.placeholderCurrentUser} size={40} />
							</Button>
						)}
					</TooltipButton>
				</Row>
			</Row>
		</div>
	);
}

function HeaderDrawerController({ isOpen, onClose }: Runners.HeaderDrawerProps) {
	if (!isOpen) {
		return null;
	}
	return <HeaderDrawer onClose={onClose} />;
}

function HeaderDrawer({ onClose }: Pick<Runners.HeaderDrawerProps, "onClose">) {
	const isOnDesktop = useMediaQuery("(min-width: 800px)");
	const zIndex = useMaxStackIndex();

	React.useEffect(() => {
		if (isOnDesktop) {
			onClose();
		}
	}, [onClose, isOnDesktop]);

	const onNavigateClick = React.useCallback(
		(event: React.MouseEvent | React.KeyboardEvent) => {
			if (event.ctrlKey || event.metaKey) {
				return;
			}
			onClose();
		},
		[onClose],
	);

	return (
		<Drawer location="left" isModal onClose={onClose} zIndex={zIndex} className={styles["header__drawer"]}>
			<Drawer.Body className={styles["header__drawer__body"]}>
				<Column justify="spaced-start" style={{ flexGrow: 1 }}>
					<ModelComponents.OmniCreateButtonPermissionRequired>
						<ModelComponents.OmniCreateButton className={styles["header__drawer__omni-create"]}>
							<Row justify="spaced-start" align="center">
								<Icon.Plus />
								<span className={styles["header__drawer__omni-create__text"]}>Create</span>
								<div className={styles["header__drawer__omni-create__icon-placeholder"]}></div>
							</Row>
						</ModelComponents.OmniCreateButton>
					</ModelComponents.OmniCreateButtonPermissionRequired>

					<nav className={styles["header__drawer__nav"]}>
						<Column justify="space-between" style={{ flexGrow: 1 }}>
							<Column justify="spaced-start">
								<NavLink
									to={homePageInfo.to}
									exact={homePageInfo.exact}
									onClick={onNavigateClick}
									className={styles["header__drawer__nav__link"]}
								>
									<Row justify="spaced-start" align="center">
										<Icon.House />
										<span className={styles["header__drawer__nav__link__text"]}>Home</span>
										<div className={styles["header__drawer__nav__link__icon-placeholder"]}></div>
									</Row>
								</NavLink>
								<NavLink
									to={messagingPageInfo.to}
									exact={messagingPageInfo.exact}
									onClick={onNavigateClick}
									className={styles["header__drawer__nav__link"]}
								>
									<Row justify="spaced-start" align="center">
										<span className={styles["header__drawer__nav__link__icon__wrapper"]}>
											<MessageComponents.ControlledMessageAlertIcon
												messagingPagePath={messagingPageInfo.path as string}
												className={styles["header__drawer__nav__link__icon__alert"]}
											/>
											<Icon.Chat />
										</span>
										<span className={styles["header__drawer__nav__link__text"]}>Messaging</span>
										<div className={styles["header__drawer__nav__link__icon-placeholder"]}></div>
									</Row>
								</NavLink>
								<NavLink
									to={preferencesPageInfo.to}
									exact={preferencesPageInfo.exact}
									onClick={onNavigateClick}
									className={styles["header__drawer__nav__link"]}
								>
									<Row justify="spaced-start" align="center">
										<Icon.Gear />
										<span className={styles["header__drawer__nav__link__text"]}>Preferences</span>
										<div className={styles["header__drawer__nav__link__icon-placeholder"]}></div>
									</Row>
								</NavLink>
							</Column>

							<Column justify="spaced-start" style={{ marginTop: "2rem" }}>
								<NavLink
									to={logoutPageInfo.to}
									exact={logoutPageInfo.exact}
									onClick={onNavigateClick}
									className={styles["header__drawer__nav__link"]}
								>
									<Row justify="spaced-start" align="center">
										<Icon.SignOut />
										<span className={styles["header__drawer__nav__link__text"]}>Logout</span>
										<div className={styles["header__drawer__nav__link__icon-placeholder"]}></div>
									</Row>
								</NavLink>
							</Column>
						</Column>
					</nav>
				</Column>
			</Drawer.Body>
		</Drawer>
	);
}

function HeaderSideBar({ isOpen }: Runners.HeaderSideBarProps) {
	if (!isOpen) {
		return null;
	}

	return (
		<Drawer
			location="right"
			zIndex={1}
			className={styles["header__side-bar"]}
			wrapperClassName={styles["header__side-bar__wrapper"]}
		>
			<Drawer.Body> </Drawer.Body>
		</Drawer>
	);
}
